// ─── Enums ────────────────────────────────────────────────────────────────────

export enum OrganizationLevel {
  Generalsekretariat = 'Generalsekretariat',
  Landesverband = 'Landesverband',
  Bereich = 'Bereich',
  Bezirksstelle = 'Bezirksstelle',
}

export enum UserRole {
  ADMIN_GENERALSEKRETARIAT = 'ADMIN_GENERALSEKRETARIAT',
  ADMIN_LANDESVERBAND = 'ADMIN_LANDESVERBAND',
  ADMIN_BEREICH = 'ADMIN_BEREICH',
  ADMIN_BEZIRKSSTELLE = 'ADMIN_BEZIRKSSTELLE',
  USER_EDITOR = 'USER_EDITOR',
  USER_VIEWER = 'USER_VIEWER',
  USER_INPUT = 'USER_INPUT',
}

export enum DataFieldType {
  Text = 'text',
  Number = 'number',
  Boolean = 'boolean',
  Date = 'date',
  DateTime = 'datetime',
  Select = 'select',
  MultiSelect = 'multiselect',
  File = 'file',
  Image = 'image',
  POI = 'POI',
  POI_LIST = 'POI_LIST',
  GeoPoint = 'geopoint',
  GeoShape = 'geoshape',
}

export enum CoordinateSystem {
  WGS84 = 'wgs84',
  EPSG31256 = 'epsg31256',
  EPSG31257 = 'epsg31257',
}

export enum ResetInterval {
  NEVER = "NEVER",
  DAILY = "DAILY",
  WEEKLY = "WEEKLY",
  MONTHLY = "MONTHLY",
  YEARLY = "YEARLY",
}

export enum AggregationMode {
  None = 'OVERWRITE',
  Sum = 'SUM',
  Average = 'AVERAGE',
  Count = 'COUNT',
  Append = 'APPEND',
  LastValue = 'LAST_VALUE',
}

export enum ApiDirection {
  Inbound = 'inbound',
  Outbound = 'outbound',
  Bidirectional = 'bidirectional',
}

export enum ApiProtocol {
  REST = 'rest',
  SOAP = 'soap',
  GraphQL = 'graphql',
  Webhook = 'webhook',
  MQTT = 'mqtt',
}

export enum QuestionnaireStatus {
  Draft = 'DRAFT',
  Active = 'ACTIVE',
  Archived = 'ARCHIVED',
}

export enum DataEntryStatus {
  Draft = 'DRAFT',
  Submitted = 'SUBMITTED',
  Approved = 'APPROVED',
  Rejected = 'REJECTED',
  Archived = 'ARCHIVED',
}

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface Organization {
  id: string;
  name: string;
  shortName?: string;
  level: OrganizationLevel;
  parentId: string | null;
  parent?: Organization | null;
  children?: Organization[];
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  alarmplanEnabled?: boolean;
}

export interface User {
  id: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  displayName: string;
  role: UserRole;
  organizationId: string;
  organization?: Organization;
  enabled: boolean;
  lastLogin: string | null;
  createdAt: string;
  updatedAt: string;
  alarmplanEnabled?: boolean;
}

export interface Cluster {
  id: string;
  name: string;
  description?: string | null;
  color?: string | null;
  organizationId?: string | null;
  visibleToChildren?: boolean;
  createdAt?: string;
  updatedAt?: string;
  _count?: { questionnaires: number; dataFields: number };
}

export interface ClusterAssignment {
  clusterId: string;
  cluster: Cluster;
}

export interface DataField {
  id: string;
  organizationId: string;
  organization?: Organization;
  name: string;
  key: string;
  label: string;
  description: string | null;
  type: string;
  isRequired: boolean;
  hasPersonReference?: boolean;
  isSensitive?: boolean;
  isUnique: boolean;
  isActive: boolean;
  sortOrder: number;
  options: string[] | null;
  validationRules: Record<string, unknown>;
  coordinateSystem: CoordinateSystem | null;
  aggregationMode: AggregationMode;
  resetInterval: ResetInterval;
  metadata: Record<string, unknown>;
  clusters?: ClusterAssignment[];
  createdAt: string;
  updatedAt: string;
  alarmplanEnabled?: boolean;
}

export interface QuestionnaireField {
  id: string;
  questionnaireId: string;
  fieldId: string;
  dataFieldId: string;
  dataField: DataField;
  field?: DataField;
  sortOrder: number;
  isRequired: boolean;
  hasPersonReference?: boolean;
  isSensitive?: boolean;
  section: string | null;
  helpText: string | null;
  defaultValue: string | null;
  conditionalLogic: Record<string, unknown> | null;
  label?: string | null;
}

export type AfterSubmitAction = 'SHOW_READONLY' | 'REDIRECT' | 'REFILL' | 'THANKS_TEXT';

export interface Questionnaire {
  id: string;
  organizationId: string;
  organization?: Organization;
  name: string;
  description: string | null;
  status: QuestionnaireStatus;
  version: number;
  autoSubmit?: boolean;
  allowEditWithinDay?: boolean;
  visibleToChildren?: boolean;
  afterSubmitAction?: AfterSubmitAction;
  pdfEnabled?: boolean;
  redirectUrl?: string | null;
  thanksText?: string | null;
  fields: QuestionnaireField[];
  assignments?: { id: string; user: { id: string; displayName: string } }[];
  orgAssignments?: { id: string; organization: { id: string; name: string } }[];
  clusters?: ClusterAssignment[];
  createdAt: string;
  updatedAt: string;
  alarmplanEnabled?: boolean;
}

export interface QuestionnaireAssignment {
  id: string;
  questionnaireId: string;
  questionnaire?: Questionnaire;
  organizationId: string;
  organization?: Organization;
  isMandatory: boolean;
  dueDate: string | null;
  startDate: string | null;
  endDate: string | null;
  maxSubmissions: number | null;
  createdAt: string;
  updatedAt: string;
  alarmplanEnabled?: boolean;
}

export interface DataEntry {
  id: string;
  questionnaireId: string;
  questionnaire?: Questionnaire;
  organizationId: string;
  organization?: Organization;
  submittedById: string;
  submittedBy?: User;
  status: DataEntryStatus;
  data: Record<string, unknown>;
  geoLocation: [number, number] | null;
  submittedAt: string | null;
  reviewedById: string | null;
  reviewedBy?: User | null;
  reviewedAt: string | null;
  reviewComment: string | null;
  createdAt: string;
  updatedAt: string;
  alarmplanEnabled?: boolean;
}

export interface ApiEndpoint {
  id: string;
  organizationId: string;
  organization?: Organization;
  name: string;
  direction: ApiDirection;
  protocol: ApiProtocol;
  url: string;
  isActive: boolean;
  authenticationType: string | null;
  rateLimit: number | null;
  lastCalledAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  alarmplanEnabled?: boolean;
}

export interface AuditLog {
  id: string;
  userId: string;
  user: User;
  organizationId: string;
  action: string;
  entityType: string;
  entityId: string;
  entityName?: string | null;
  changes: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  user: User;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface StatSummary {
  totalFields: number;
  totalQuestionnaires: number;
  activeApis: number;
  pendingSubmissions: number;
}


export interface AlarmplanEntry {
  id: string;
  category: AlarmplanCategory;
  position: number;
  name: string;
  telefon: string | null;
  handy: string | null;
  tetraIssi: string | null;
  satellit: string | null;
}

export enum AlarmplanCategory {
  FG_1 = 'FG_1',
  FG_2 = 'FG_2',
  FG_3 = 'FG_3',
  FG_4 = 'FG_4',
  FG_5 = 'FG_5',
  FG_6 = 'FG_6',
  FG_7 = 'FG_7',
  CDS = 'CDS',
  LRK = 'LRK',
  SPOC = 'SPOC',
  ALARMNUMMER = 'ALARMNUMMER',
}

export interface AlarmplanOrgEntries {
  organizationId: string;
  organizationName: string;
  entries: AlarmplanEntry[];
}
